import React, { useCallback, useMemo, useState } from 'react'
import { Col, Icon, List, Modal, Row, Tooltip } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  Id,
  IntegrationType,
  PhoneContact,
  Request,
  RequestTicketSelectedSituation
} from 'src/types'

import {
  AvatarListHorizontal,
  TagCurrency,
  TagTicketSituation
} from 'src/components/molecules'
import { ButtonTitled } from 'src/components/Ant'
import {
  Empty,
  Spacer,
  T,
  Text,
  TextTValue,
  useGetSelectedHotels
} from 'src/components/atoms'
import { getHotelsHistory } from 'src/core/store/ducks/hotels/actions'
import { OriginRequest } from 'src/core/store/ducks/requests/types'
import {
  saveFullRequest,
  saveRequestTickets
} from 'src/core/store/ducks/requests/actions'
import ModalFormEdit from 'src/views/Requests/views/Form/components/Steps/Services/components/Hotels/components/modal-form/index'
import ModalHistory from 'src/views/Requests/views/Form/components/Steps/Services/components/Hotels/components/modal-history'
import { PhoneMask } from 'src/core/utils/phoneMask'
import Images from 'src/config/images'

import { SelectedHotel } from 'src/components/atoms/use-get-selected-hotels'
import * as CSS from './styles'
import { ApplicationState } from 'src/store'

interface Props {
  approvals?: boolean
  originRequest?: OriginRequest
  request: Request
}

function RenderItemListHotels({
  item,
  removeTicket
}: {
  approvals?: boolean
  item: SelectedHotel
  removeTicket: (ticketId: Id) => void
}): JSX.Element {
  const {
    me: {
      user: { tenant_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const { t } = useTranslation()
  const emissionContacts: PhoneContact[] =
    item.agency_data?.agency_organization.contacts?.reduce<PhoneContact[]>(
      (acc, cur) => {
        if (cur.hasOwnProperty('phone_contacts')) {
          acc.push(
            ...{ phone_contacts: null, email_contacts: null, ...cur }
              .phone_contacts
          )
        }
        return acc
      },
      []
    ) || []

  const canRemoveTicket =
    +tenant_id === 559042084949 && typeof item.request_ticket_id === 'number'

  return (
    <CSS.HotelItem>
      <List.Item.Meta
        description={
          <>
            {/* Top */}
            <CSS.DivBordered>
              <Row>
                <Col span={24}>
                  <CSS.CardTopDiv>
                    <div>
                      <AvatarListHorizontal
                        ownerInfo={
                          item.user_data?.length > 1 &&
                          `${t('web.services.hotels.form.owner')}: `
                        }
                        users={item.user_data}
                      />
                    </div>
                    {!!item.status && (
                      <div>
                        <TagTicketSituation status={item.situation} />
                        {canRemoveTicket && (
                          <ButtonTitled
                            icon="delete"
                            key="hotel-delete"
                            onClick={() => removeTicket(item.request_ticket_id)}
                            size="default"
                            style={{ marginLeft: 8 }}
                            tooltip="common.actions.remove"
                          />
                        )}
                      </div>
                    )}
                    {!!item.eticket_code && (
                      <div>
                        <Spacer horizontal="4px" />
                        <Text
                          strong
                          suffix=":"
                          text="web.services.hotels.reservation"
                        />
                        <CSS.Code>
                          <span>
                            <Text code copyable={{ text: item.eticket_code }}>
                              {item.eticket_code}
                            </Text>
                          </span>
                        </CSS.Code>
                      </div>
                    )}
                  </CSS.CardTopDiv>
                </Col>
              </Row>
            </CSS.DivBordered>
            {/* Vehicle Infos */}
            <CSS.DivBordered>
              <Row>
                <Col span={24}>
                  <CSS.CardContentDiv>
                    {/* Title */}
                    <Row>
                      <Col span={24}>
                        <Row>
                          <Col span={24}>
                            <Tooltip title={item.brand_name}>
                              <CSS.Title>
                                <CSS.TextEllipsis>
                                  {item.brand_name}
                                </CSS.TextEllipsis>
                              </CSS.Title>
                            </Tooltip>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={24}>
                            <Tooltip title={item.address}>
                              <TextTValue text="web.services.hotels.form.address">
                                <CSS.TextEllipsis>
                                  {item.address}
                                </CSS.TextEllipsis>
                              </TextTValue>
                            </Tooltip>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <Spacer vertical="12px" />
                    {/* Image */}
                    <Row gutter={24}>
                      <Col span={8}>
                        <CSS.Image>
                          <img
                            alt="Hotel"
                            src={item.hotel_image_url}
                            style={{ maxWidth: '175px' }}
                          />
                        </CSS.Image>
                      </Col>
                      <Col span={16}>
                        <TextTValue text="web.services.hotels.form.period.start-time">
                          {item.checkin_date_format}
                        </TextTValue>
                        <TextTValue text="web.services.hotels.form.period.end-time">
                          {item.checkout_date_format}
                        </TextTValue>
                        <br />
                        {item.category_stars > 0 && (
                          <>
                            <Text
                              prefix={
                                <>
                                  <Icon type="star" />{' '}
                                </>
                              }
                              strong
                              text={{
                                key: 'web.services.hotels.form.stars_plural',
                                options: { count: item.category_stars }
                              }}
                            />
                            <br />
                          </>
                        )}
                        <Text
                          prefix="- "
                          strong
                          text={
                            item.pension
                              ? 'web.services.hotels.form.pension'
                              : 'web.services.hotels.form.no-pension'
                          }
                        />
                        <br />
                        {item.laundry && (
                          <>
                            <Text
                              prefix="- "
                              strong
                              text="web.services.hotels.form.laundry"
                            />
                            <br />
                          </>
                        )}
                        {item.parking && (
                          <>
                            <Text
                              prefix="- "
                              strong
                              text="web.services.hotels.form.parking"
                            />
                            <br />
                          </>
                        )}
                        {item.pool && (
                          <>
                            <Text
                              prefix="- "
                              strong
                              text="web.services.hotels.form.pool"
                            />
                            <br />
                          </>
                        )}
                        {item.restaurant && (
                          <>
                            <Text
                              prefix="- "
                              strong
                              text="web.services.hotels.form.restaurant"
                            />
                          </>
                        )}
                      </Col>
                    </Row>
                    <Spacer vertical="12px" />
                    {/* Infos */}
                    {item.integration_type === IntegrationType.OFFLINE &&
                      item.situation !==
                        RequestTicketSelectedSituation.ISSUED && (
                        <Row>
                          <Col span={24}>
                            <CSS.OfflineOffer>
                              {t('web.services.hotels.offline-offer')}
                            </CSS.OfflineOffer>
                          </Col>
                        </Row>
                      )}
                    <Row>
                      <Col span={16}>
                        <Row>
                          <Col>
                            <TextTValue text="web.services.hotels.form.bedroom">
                              {item.category}
                            </TextTValue>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={8}>
                        <Row>
                          <Col span={24}>
                            <CSS.Currency>
                              <TagCurrency value={item?.total_value} />
                            </CSS.Currency>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </CSS.CardContentDiv>
                </Col>
              </Row>
            </CSS.DivBordered>
            {/* Emission infos */}
            <CSS.DivBordered>
              <CSS.AgencyInfo>
                <div>
                  <TextTValue text="web.agencies.Issuer">
                    {item.agency_data?.agency_organization.trade_name}
                  </TextTValue>
                  {emissionContacts.map((phone_contacts, index) => (
                    <div key={index}>
                      <Text
                        strong
                        suffix=": "
                        text="web.agencies.contacts.phone"
                      />
                      <Text
                        code
                        copyable={{
                          text: phone_contacts.phone_number
                        }}
                      >
                        {`${phone_contacts.calling_code} ${PhoneMask(
                          phone_contacts.phone_number
                        )}`}
                      </Text>
                      {phone_contacts.is_whatsapp && (
                        <CSS.IconWhatsApp
                          alt="WhatsApp"
                          src={Images.logoWhatsSmall}
                          onClick={() => {
                            window.open(
                              `https://wa.me/${phone_contacts.phone_number}`
                            )
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <TagCurrency value={item.total_value} />
                </div>
              </CSS.AgencyInfo>
            </CSS.DivBordered>
          </>
        }
      />
    </CSS.HotelItem>
  )
}

function HotelsSummary({
  approvals,
  request,
  originRequest
}: Props): JSX.Element {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [visibleModalHistory, setVisibleModalHistory] = useState<boolean>(false)
  const [visibleModalEdit, setVisibleModalEdit] = useState<boolean>(false)

  const selectedHotels = useGetSelectedHotels(request)

  function handleRemoveTicket(id: Id) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.approvals.modal-remove-ticket'),
      okText: t('common.actions.yes'),
      onCancel() {},
      onOk: () => {
        return new Promise(() => {
          const foundTicket = request.request_tickets.find(
            ticket => +ticket.request_ticket_id === +id
          )
          if (foundTicket) {
            dispatch(saveRequestTickets([{ ...foundTicket, active: false }]))
          }
        })
      },
      title: t('common.are-you-sure')
    })
  }

  const handleDeleteTicketsHotel = useCallback(
    () => {
      Modal.confirm({
        cancelText: t('common.actions.cancel'),
        content: t('web.approvals.modal-remove-hotel'),
        okText: t('common.actions.yes'),
        onCancel() {},
        onOk: async () => {
          return new Promise(() => {
            dispatch(
              saveFullRequest({
                ...request,
                request_tickets: request.request_tickets.map(ticket =>
                  ticket.request_ticket_type !== 'HOTEL'
                    ? { ...ticket }
                    : {
                        ...ticket,
                        active: false
                      }
                )
              })
            )
          })
        },
        title: t('common.are-you-sure')
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request]
  )

  const extraButtons = useMemo(() => {
    if (selectedHotels.length <= 0) {
      return [
        approvals && (
          <ButtonTitled
            icon="plus"
            key="hotel-new"
            onClick={() => setVisibleModalEdit(true)}
            size="default"
            style={{ marginLeft: 8 }}
            tooltip="common.actions.add"
          />
        )
      ]
    }

    return [
      approvals && (
        <ButtonTitled
          icon="edit"
          key="hotel-edit"
          onClick={() => setVisibleModalEdit(true)}
          size="default"
          style={{ marginLeft: 8 }}
          tooltip="common.actions.edit"
        />
      ),
      approvals && (
        <ButtonTitled
          icon="delete"
          key="hotel-delete"
          onClick={handleDeleteTicketsHotel}
          size="default"
          style={{ marginLeft: 8 }}
          tooltip="common.actions.remove-all"
        />
      ),
      typeof request.request_id === 'number' && (
        <ButtonTitled
          key="hotel-history"
          icon="history"
          onClick={() => {
            if (request?.request_id) {
              dispatch(getHotelsHistory({ request_id: request.request_id }))
            }
            setVisibleModalHistory(true)
          }}
          size="default"
          tooltip="common.actions.historical"
          style={{ marginLeft: 8 }}
        />
      )
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, selectedHotels])

  return (
    <>
      <CSS.Card
        data-test-id="summary-hotels"
        bodyStyle={{ paddingTop: 0, minHeight: 220 }}
        extra={extraButtons}
        title={<T text="web.services.hotels.title" />}
      >
        <List
          dataSource={selectedHotels}
          itemLayout="horizontal"
          locale={{ emptyText: <Empty /> }}
          renderItem={(item, index) => (
            <RenderItemListHotels
              approvals={approvals}
              removeTicket={handleRemoveTicket}
              item={item}
              key={index}
            />
          )}
        />
      </CSS.Card>
      <ModalHistory
        toggle={() => {
          setVisibleModalHistory(false)
        }}
        visible={visibleModalHistory}
      />
      <ModalFormEdit
        approvals={approvals}
        request={request}
        toggle={() => {
          setVisibleModalEdit(false)
        }}
        visible={visibleModalEdit}
      />
    </>
  )
}

export default HotelsSummary
